import { BrowserProvider } from "ethers";
import posthog from 'posthog-js';
import React, { useEffect, useState } from 'react';
import { API_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { magic } from "../lib/magic";
import { placeParlayOnchain } from '../lib/onchain';
import { ParlaySuggestion } from '../services/api';
import '../styles/ParlaySuggestionCard.css';
import LoadingDots from './LoadingDots';
import LoginModal from './LoginModal';

interface ParlaySuggestionCardProps {
  suggestion: ParlaySuggestion;
  onTakeParlay: (suggestion: ParlaySuggestion) => void;
  onParlayPlaced?: (suggestion: ParlaySuggestion) => void;
  currentIndex?: number;
  totalSuggestions?: number;
  onDotClick?: (index: number) => void;
}

const ParlaySuggestionCard: React.FC<ParlaySuggestionCardProps> = ({ suggestion, onTakeParlay, onParlayPlaced, currentIndex = 0, totalSuggestions = 1, onDotClick }) => {
  const { isAuthenticated, user } = useAuth();
  const [showStakeInput, setShowStakeInput] = useState(false);
  const [stakeAmount, setStakeAmount] = useState<number>(0);
  const [isQuoting, setIsQuoting] = useState(false);
  const [isPlacing, setIsPlacing] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  // Reset state when switching to a different suggestion card
  useEffect(() => {
    setShowStakeInput(false);
    setStakeAmount(0);
    setErrorMsg(null);
    setIsQuoting(false);
    setIsPlacing(false);
    setIsLoginModalOpen(false);
    setIsRetrying(false);
    setFailedImages(new Set());
  }, [currentIndex]);

  // Input validation functions
  const validateSuggestion = (suggestion: ParlaySuggestion): string | null => {
    if (!suggestion) return "Invalid suggestion data";
    if (!suggestion.legs || suggestion.legs.length === 0) return "No parlay legs available";
    if (!suggestion.total_odds || suggestion.total_odds <= 0) return "Invalid odds data";
    
    for (const leg of suggestion.legs) {
      if (!leg.ingest_market_id) return "Missing market ID for one or more legs";
      if (!leg.outcome_label) return "Missing outcome label for one or more legs";
      if (leg.outcome_price === undefined || leg.outcome_price === null) return "Missing price data for one or more legs";
    }
    
    return null;
  };

  const validateStakeAmount = (amount: number): string | null => {
    if (amount <= 0) return "Stake amount must be greater than 0";
    if (amount < 0.01) return "Minimum stake amount is $0.01";
    if (amount > 10000) return "Maximum stake amount is $10,000";
    if (!Number.isFinite(amount)) return "Invalid stake amount";
    const cents = Math.round(amount * 100);
    if (Math.abs(amount * 100 - cents) > 0.01) return "Stake amount can only have up to 2 decimal places";

    return null;
  };

  const validateWalletAddress = (address: string): boolean => {
    return /^0x[a-fA-F0-9]{40}$/.test(address);
  };

  const formatNumberWithCommas = (num: number) => {
    return num.toLocaleString();
  };

  const formatPrice = (price: number) => {
    const p = price || 0;
    if (p > 0 && p < 0.01) {
      const cents = Math.floor(p * 10000) / 100;
      const val = cents <= 0 ? 0.01 : cents;
      return val.toFixed(2);
    }
    if (p >= 0.99 && p < 1) {
      const truncatedTwoDecimals = Math.floor(p * 10000) / 100;
      return truncatedTwoDecimals.toFixed(2);
    }
    return Math.round(p * 100).toString();
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'TBD';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const calculateSettlementDate = () => {
    const validDates = suggestion.legs
      .map(leg => leg.end_date ? new Date(leg.end_date).getTime() : 0)
      .filter(time => time > 0);

    if (validDates.length === 0) return 'TBD';
    const latestTime = Math.max(...validDates);
    return formatDate(new Date(latestTime).toISOString());
  };

  async function getWalletAddressFallback(): Promise<string | undefined> {
    try {
      const provider = new BrowserProvider(magic.rpcProvider as any);
      const signer = await provider.getSigner();
      return await signer.getAddress();
    } catch {
      return undefined;
    }
  }

  function toEngineLegs() {
    return suggestion.legs.map(l => ({
      ingest_market_id: l.ingest_market_id,
      outcome_label: l.outcome_label
    }));
  }

  const createAbortController = () => {
    return new AbortController();
  };

  const isNetworkError = (error: any): boolean => {
    return error.name === 'TypeError' && 
           (error.message.includes('fetch') || error.message.includes('network') || error.message.includes('Failed to fetch'));
  };

  const isTimeoutError = (error: any): boolean => {
    return error.name === 'AbortError' || error.message.includes('timeout');
  };

  async function requestQuote(stake: number, retryCount: number = 0): Promise<any> {
    let walletAddr = user?.walletAddress || (await getWalletAddressFallback());
    
    // If gas sponsorship is available, use smart account address for quotes
    if (user?.gasSponsored && user?.smartAccountAddress) {
      walletAddr = user.smartAccountAddress;
      console.log('🚀 Using smart account address for quote:', walletAddr);
    }
    
    if (!walletAddr) throw new Error("No wallet address found.");
    if (!user?.issuer) throw new Error("Missing user issuer for quoting.");

    // Validate wallet address format
    if (!validateWalletAddress(walletAddr)) {
      throw new Error("Invalid wallet address format.");
    }

    const payload = {
      issuer: user.issuer,
      smart_contract_wallet_address: walletAddr,
      stake,
      legs: toEngineLegs()
    };

    const controller = createAbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

    try {
      const res = await fetch(`${API_URL}/quote`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!res.ok) {
        const text = await res.text();
        let errorMessage = `/quote failed: ${res.status}`;
        
        if (res.status === 429) {
          errorMessage = "Too many requests. Please wait a moment and try again.";
        } else if (res.status >= 500) {
          errorMessage = "Server error. Please try again in a moment.";
        } else if (res.status === 400) {
          errorMessage = "Invalid request. Please check your stake amount.";
        } else if (text) {
          errorMessage = text;
        }
        
        throw new Error(errorMessage);
      }

      const response = await res.json();
      
      // Validate response structure
      if (!response || typeof response !== 'object') {
        throw new Error("Invalid response format from server.");
      }

      return response;
      
    } catch (error: any) {
      clearTimeout(timeoutId);
      
      if (isTimeoutError(error)) {
        throw new Error("Request timed out. Please check your connection and try again.");
      }
      
      if (isNetworkError(error)) {
        if (retryCount < 1) {
          setIsRetrying(true);
          await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds
          return requestQuote(stake, retryCount + 1);
        }
        throw new Error("Network error. Please check your connection and try again.");
      }
      
      throw error;
    } finally {
      if (retryCount === 0) {
        setIsRetrying(false);
      }
    }
  }

  const handleTakeParlay = () => {
    if (!isAuthenticated) {
      setIsLoginModalOpen(true);
      return;
    }
    try {
      posthog.capture('engagement:suggestion_taken', {
        suggestion_id: `${currentIndex}`,
        stake_amount: 0,
        total_odds: suggestion.total_odds,
      });
    } catch {}
    setShowStakeInput(true);
  };

  const handleBlockchainError = (error: any): string => {
    if (error.code === 'INSUFFICIENT_FUNDS') {
      return "Insufficient funds for transaction. Please check your wallet balance.";
    }
    if (error.code === 'UNPREDICTABLE_GAS_LIMIT') {
      return "Transaction failed due to gas estimation. Please try again.";
    }
    if (error.code === 'USER_REJECTED') {
      return "Transaction was cancelled by user.";
    }
    // Handle gas sponsorship failures specifically
    if (error.name === 'GasSponsorshipError' || error.message?.includes('Gas sponsorship failed')) {
      return "Gas sponsorship failed after multiple attempts. Please try again.";
    }
    if (error.message?.includes('gas')) {
      return "Transaction failed due to gas issues. Please try again.";
    }
    if (error.message?.includes('revert')) {
      return "Transaction was reverted. The parlay may no longer be available.";
    }
    if (error.message?.includes('timeout')) {
      return "Transaction timed out. Please check your network connection.";
    }
    return error.message || "Blockchain transaction failed. Please try again.";
  };

  const handlePlaceParlay = async () => {
    setErrorMsg(null);

    // Validate suggestion data first
    const suggestionError = validateSuggestion(suggestion);
    if (suggestionError) {
      setErrorMsg(suggestionError);
      return;
    }

    if (!isAuthenticated) {
      setIsLoginModalOpen(true);
      return;
    }

    // Validate stake amount
    const stakeError = validateStakeAmount(stakeAmount);
    if (stakeError) {
      setErrorMsg(stakeError);
      return;
    }

    // Prevent concurrent operations
    if (isQuoting || isPlacing) {
      setErrorMsg("Please wait for the current operation to complete.");
      return;
    }

    try {
      setIsQuoting(true);
      // Track quote requested for suggestion flow
      try {
        posthog.capture('parlay:quote_requested', {
          stake: stakeAmount,
          legs_count: suggestion.legs.length,
          source: 'suggestion_card',
        });
      } catch {}
      const quoteResp = await requestQuote(stakeAmount);
      setIsQuoting(false);

      // Validate quote response structure
      if (!quoteResp?.quote_struct || !quoteResp?.houseSig) {
        throw new Error("Backend did not return valid quote data. Please try again.");
      }

      // Validate quote_struct has required fields
      const { quote_struct } = quoteResp;
      if (!quote_struct.expiry || !quote_struct.stake || !quote_struct.expectedPayout) {
        throw new Error("Invalid quote structure received from server.");
      }

      const currentTimestamp = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = quote_struct.expiry - currentTimestamp;

      // Handle expired quotes
      if (timeUntilExpiry <= 0) {
        throw new Error("Quote has expired. Please try again.");
      }

      let placed: any = null;
      let finalQuoteResp = quoteResp;

      if (timeUntilExpiry < 30) {
        // Get fresh quote if current one is about to expire
        const freshQuoteResp = await requestQuote(stakeAmount);
        finalQuoteResp = freshQuoteResp;
      }

      // Track quote shown (to-win visible in this flow after quote returns)
      try {
        const shownStake = (finalQuoteResp?.quote_struct?.stake ?? stakeAmount);
        const expectedPayout = (finalQuoteResp?.quote_struct?.expectedPayout ?? (shownStake * (suggestion.total_odds || 1)));
        posthog.capture('parlay:quote_shown', {
          expected_payout: expectedPayout,
          total_odds: (finalQuoteResp?.total_odds ?? suggestion.total_odds),
          stake: shownStake,
          legs_count: suggestion.legs.length,
          source: 'suggestion_card',
        });
      } catch {}

      setIsPlacing(true);
      
      try {
        placed = await placeParlayOnchain(finalQuoteResp);
      } catch (blockchainError: any) {
        throw new Error(handleBlockchainError(blockchainError));
      }

      // Validate transaction result
      if (!placed || !placed.txHash) {
        throw new Error("Transaction was not confirmed. Please check your wallet.");
      }

      // Record the ticket with timeout handling
      const recordController = createAbortController();
      const recordTimeoutId = setTimeout(() => recordController.abort(), 10000); // 10 second timeout

      try {
        const recordRes = await fetch(`${API_URL}/tickets/record`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            quoteId: finalQuoteResp.quoteId,
            txHash: placed.txHash
          }),
          signal: recordController.signal
        });

        clearTimeout(recordTimeoutId);

        if (!recordRes.ok) {
          console.warn("Failed to record ticket, but parlay was placed successfully");
        }
      } catch (recordError) {
        clearTimeout(recordTimeoutId);
        console.warn("Failed to record ticket, but parlay was placed successfully:", recordError);
      }

      // Success - reset state and notify parent
      setShowStakeInput(false);
      setStakeAmount(0);
      setErrorMsg(null);
      
      // Notify parent component that parlay was placed
      if (onParlayPlaced) {
        onParlayPlaced(suggestion);
      }
    } catch (e: any) {
      console.error('Parlay placement error:', e);
      setErrorMsg(e?.message || "Failed to place parlay. Please try again.");
    } finally {
      setIsQuoting(false);
      setIsPlacing(false);
    }
  };

  const handleLoginSuccess = async () => {
    setIsLoginModalOpen(false);
    if (!showStakeInput) {
      setShowStakeInput(true);
    } else {
      handlePlaceParlay();
    }
  };

  // Early validation - don't render if suggestion is invalid
  const suggestionError = validateSuggestion(suggestion);
  if (suggestionError) {
    return (
      <div className="parlay-suggestion-card">
        <div className="card-content">
          <div className="error-message" style={{ margin: '20px', textAlign: 'center' }}>
            {suggestionError}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="parlay-suggestion-card">
      <div className="card-content">
        <div className="parlay-details">
          <div className="suggestion-header">
            <h3>Popular Parlay</h3>
            <img src="/TPC_only.png" alt="TPC" className="suggestion-logo" />
          </div>

          <div className="suggestion-settlement">
            Settlement Date: {calculateSettlementDate()}
          </div>

          <div className="suggestion-legs">
            {suggestion.legs.map((leg, index) => {
              // Extract team logo if this is a sports bet
              let legIcon = leg.market_icon;
              if (leg.sportsTeamData && leg.sportsTeamData.selectedTeam > 0) {
                const selectedTeam = leg.sportsTeamData.selectedTeam === 1
                  ? leg.sportsTeamData.team1
                  : leg.sportsTeamData.team2;
                if (selectedTeam.logo) {
                  legIcon = selectedTeam.logo;
                }
              }

              return (
                <div key={index} className="suggestion-leg">
                  {legIcon && (
                    <img
                      src={(() => {
                        if (legIcon && failedImages.has(legIcon)) {
                          return leg.event_image || legIcon;
                        }
                        return legIcon;
                      })()}
                      alt={leg.event_title}
                      className="leg-icon"
                      onError={(e) => {
                        if (legIcon && !failedImages.has(legIcon)) {
                          const newFailedImages = new Set(failedImages);
                          newFailedImages.add(legIcon);
                          setFailedImages(newFailedImages);
                        }
                      }}
                    />
                  )}
                  <div className="leg-info">
                    <div className="leg-title">{leg.event_title || leg.market_question}</div>
                    <div className="leg-question">
                      {leg.event_title && leg.market_question !== leg.event_title ? leg.market_question : ''}
                    </div>
                  </div>
                  <div className="leg-outcome-price">
                    {formatPrice(leg.outcome_price)}¢
                  </div>
                  <span className={`leg-outcome-badge ${leg.outcome_label.toLowerCase()}`}>
                    {leg.outcome_label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className={`payout-section ${showStakeInput ? 'stake-input-mode' : ''}`}>
          {!showStakeInput ? (
            <>
              <div className="hundred-dollar-payout">
                <span className="payout-prefix">$100 → </span>
                <span className="payout-amount-green">${formatNumberWithCommas(parseFloat((suggestion.hundred_dollar_payout || (100 * suggestion.total_odds)).toFixed(0)))}</span>
              </div>
              <button
                className="take-parlay-button"
                onClick={handleTakeParlay}
              >
                Take Parlay
              </button>
            </>
          ) : (
            <>
              <div className="stake-input-section">
                <label className="stake-label">Stake</label>
                <input 
                  type="text" 
                  className={`stake-input ${stakeAmount > 0 ? 'has-value' : ''}`}
                  value={stakeAmount > 0 ? `$${formatNumberWithCommas(stakeAmount)}` : ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[$,]/g, '');
                    const numValue = Number(value) || 0;
                    setStakeAmount(numValue);
                  }}
                  placeholder="$0"
                />
              </div>
              
              {stakeAmount > 0 && (
                <div className="calculated-payout">
                  <label className="payout-label">To win</label>
                  <div className="payout-display">
                    <div className="payout-amount">
                      ${formatNumberWithCommas(Number((stakeAmount * suggestion.total_odds).toFixed(2)))}
                    </div>
                    <div className="payout-odds">
                      ({suggestion.total_odds.toFixed(2)}x)
                    </div>
                  </div>
                </div>
              )}

              {errorMsg && <div className="error-message">{errorMsg}</div>}

              <button
                className="take-parlay-button place-parlay"
                disabled={isQuoting || isPlacing || stakeAmount <= 0}
                onClick={handlePlaceParlay}
              >
                {(isQuoting || isPlacing) ? (
                  <LoadingDots className="button-loading" />
                ) : (
                  'Place Parlay'
                )}
              </button>

              {/* Terms text - only show when button is "Place Parlay" */}
              <div className="parlay-suggestion-terms-text">
                By placing, you agree to the <span 
                  className="parlay-suggestion-terms-link"
                  onClick={() => window.open('https://docs.google.com/document/d/1ybQTWpo4lbXmDNc1tYCisBrtYXNWuiKoGW1EKIxXoo4/edit?tab=t.0', '_blank')}
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                >Terms of Use</span>
              </div>
            </>
          )}
        </div>
      </div>
      
      {totalSuggestions > 1 && onDotClick && (
        <div className="card-dots">
          {Array.from({ length: totalSuggestions }, (_, index) => (
            <button
              key={index}
              className={`card-dot ${index === currentIndex ? 'active' : ''}`}
              onClick={() => onDotClick(index)}
            />
          ))}
        </div>
      )}
      
      <LoginModal isOpen={isLoginModalOpen} onClose={handleLoginSuccess} />
    </div>
  );
};

export default ParlaySuggestionCard;
